import { msalInstance } from '../auth/msalInstance';
import { loginRequest } from '../auth/msalConfig';

class TokenService {
  private refreshTimer: NodeJS.Timeout | null = null;
  private readonly REFRESH_BUFFER = 5 * 60 * 1000; // 5 minutes before expiry

  async getAccessToken(scopes?: string[]) {
    try {
      const accounts = msalInstance.getAllAccounts();
      if (!accounts.length) {
        throw new Error('No active account');
      }

      const request = {
        ...loginRequest,
        scopes: scopes || loginRequest.scopes,
        account: accounts[0],
      };

      const response = await msalInstance.acquireTokenSilent(request);
      
      // Schedule next refresh if expiresOn is available
      if (response.expiresOn) {
        this.scheduleTokenRefresh(response.expiresOn);
      }
      
      return response.accessToken;
    } catch (error) {
      console.error('Token acquisition failed:', error);
      
      // Try interactive login as fallback
      try {
        const response = await msalInstance.acquireTokenPopup({
          ...loginRequest,
          scopes: scopes || loginRequest.scopes,
        });
        
        // Schedule next refresh if expiresOn is available
        if (response.expiresOn) {
          this.scheduleTokenRefresh(response.expiresOn);
        }
        
        return response.accessToken;
      } catch (popupError) {
        console.error('Interactive login failed:', popupError);
        throw popupError;
      }
    }
  }

  private scheduleTokenRefresh(expiresOn: Date) {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    const now = new Date();
    const timeUntilExpiry = expiresOn.getTime() - now.getTime();
    const refreshTime = Math.max(timeUntilExpiry - this.REFRESH_BUFFER, 0);

    this.refreshTimer = setTimeout(async () => {
      try {
        await this.getAccessToken();
      } catch (error) {
        console.error('Automatic token refresh failed:', error);
      }
    }, refreshTime);
  }

  clearRefreshTimer() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  // Method for manual testing
  async manualRefresh() {
    try {
      console.log('🧪 Manual token refresh initiated...');
      const token = await this.getAccessToken();
      console.log('✅ Manual token refresh successful');
      return token;
    } catch (error) {
      console.error('❌ Manual token refresh failed:', error);
      throw error;
    }
  }
}

export const tokenService = new TokenService();
